import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import _Button from '../../../components/ui/Button';
import _Input from '../../../components/ui/Input';
import _Icon from '../../../components/AppIcon';
import authService from '../../../utils/authService';

const LoginForm = ({ _onSocialLogin, isLoading, setIsLoading, onForgotPassword, _meta }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, authError, clearError, _resetPassword, _userProfile } = useAuth();

  // Check if user just completed email verification
  const verificationMessage = location.state?.message;
  const verificationEmail = location.state?.email;
  const justVerified = location.state?.justVerified;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);

  // Pre-fill email if user just verified
  useEffect(() => {
    if (verificationEmail && justVerified) {
      setFormData(prev => ({ ...prev, email: verificationEmail }));
      
      // Show verification success message
      if (verificationMessage) {
        setErrors({
          success: verificationMessage
        });
        
        // Clear the message after 5 seconds
        setTimeout(() => {
          setErrors(prev => ({ ...prev, success: '' }));
        }, 5000);
      }
    }
  }, [verificationEmail, verificationMessage, justVerified]);

  // Test connection and notification service on component mount
  useEffect(() => {
    const runDiagnostics = async () => {
      try {
        // Test Supabase connection
        const connectionResult = await authService.testConnection();
        setConnectionStatus(connectionResult);

        // Test notification service
        const notificationHealth = await authService.checkNotificationService();
        console.log('Notification service health check:', notificationHealth);

        // Update debug info with more detailed information
        setDebugInfo({
          supabaseConnected: connectionResult.connected,
          notificationService: notificationHealth.available,
          notificationMessage: notificationHealth.message || '',
          isDevelopment: notificationHealth.environment === 'development',
          functionExists: notificationHealth.functionExists || false,
          timestamp: new Date().toISOString()
        });

        // Only show connection error if not in development mode
        if (!connectionResult.connected && !debugInfo.isDevelopment) {
          setErrors({
            connection: connectionResult.error
          });
        }
      } catch (error) {
        console.error('Diagnostics failed:', error);
        setErrors({
          connection: 'Failed to run connection diagnostics'
        });
      }
    };

    runDiagnostics();
  }, [debugInfo.isDevelopment]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (authError) {
      clearError();
    }
    // Clear connection error when user interacts
    if (errors.connection) {
      setErrors((prev) => ({ ...prev, connection: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getErrorMessage = (result) => {
    if (result?.type) {
      switch (result.type) {
        case 'network_error':
          return 'Cannot connect to the server. Please check your internet connection and try again. If the issue persists, the Supabase project may be paused.';
        case 'invalid_credentials':
          return 'Invalid email or password. Please check your credentials and try again.';
        case 'user_not_found':
          return 'No account found with this email. Please check your email or create a new account.';
        case 'email_not_confirmed':
          return 'Please check your email and click the confirmation link before signing in.';
        case 'email_not_verified':
          return 'Please verify your email address before signing in. Check your email for a verification link.';
        case 'rate_limited':
          return 'Too many login attempts. Please wait a few minutes before trying again.';
        case 'schema_error':
          return 'Database configuration issue. Please contact support or check if migrations have been run.';
        case 'profile_creation_failed':
          return 'Login successful but there was an issue setting up your profile. Please contact support.';
        default:
          return result.error || 'Login failed. Please try again.';
      }
    }
    return result?.error || authError || 'Login failed. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Check connection before attempting login
    if (connectionStatus && !connectionStatus.connected) {
      setErrors({
        submit: 'Cannot connect to the authentication service. Please check your internet connection and try again.'
      });
      return;
    }

    setIsLoading(true);
    clearError();
    setErrors({});

    try {
      const result = await signIn(formData.email.trim(), formData.password);

      if (result?.success) {
        // Success - login completed successfully
        setErrors({
          success: 'Login successful! Redirecting to your dashboard...'
        });

        // Wait a moment for profile to load, then navigate to root (DashboardRedirector will handle the rest)
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      } else {
        // Handle login failure
        setErrors({
          submit: getErrorMessage(result),
          submitType: result?.type // Store the error type for special handling
        });
      }
    } catch (error) {
      // Log error for monitoring in production
      if (import.meta.env.PROD) {
        console.error('Login error:', error.message);
      } else {
        console.error('Login error:', error);
      }
      setErrors({
        submit: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    if (onForgotPassword) {
      onForgotPassword();
    }
  };

  const handleRetryConnection = async () => {
    setErrors({});
    const result = await authService.testConnection();
    setConnectionStatus(result);

    if (!result.connected) {
      setErrors({
        connection: result.error
      });
    } else {
      setErrors({
        success: 'Connection restored successfully!'
      });
    }
  };

  const handleResendVerification = async (email) => {
    if (!email || !email.trim()) {
      setResendStatus({
        success: false,
        message: 'Please enter your email address first.'
      });
      return;
    }

    setIsResending(true);
    setResendStatus(null);

    try {
      const result = await authService.resendVerificationEmail(email.trim());
      
      if (result.success) {
        setResendStatus({
          success: true,
          message: 'Verification email sent! Please check your inbox.'
        });
      } else {
        setResendStatus({
          success: false,
          message: result.error || 'Failed to send verification email. Please try again.'
        });
      }
    } catch (error) {
      // Log error for monitoring in production
      if (import.meta.env.PROD) {
        console.error('Resend verification error:', error.message);
      } else {
        console.error('Error resending verification email:', error);
      }
      setResendStatus({
        success: false,
        message: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsResending(false);
    }
  };

  const displayError = errors.submit || errors.connection || '';
  const displaySuccess = errors.success;

  // Derive email verification error state from both submitType and error message content
  // This ensures the resend section shows even if submitType is lost during state update cycles
  const isEmailVerificationError = 
    errors.submitType === 'email_not_confirmed' || 
    errors.submitType === 'email_not_verified' ||
    (displayError && (
      displayError.includes('confirmation link') || 
      displayError.includes('verify your email') ||
      displayError.includes('email verification')
    ));

  useEffect(() => {
    if (authError) {
      setErrors(prev => ({ ...prev, submit: authError }));
    }
  }, [authError]);

  return (
    <div className="space-y-6">

      {/* Connection Status */}
      {connectionStatus && !connectionStatus.connected &&
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <_Icon name="AlertTriangle" size={20} className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-800 mb-1">Connection Issue</h4>
              <p className="text-sm text-red-700 mb-3">{connectionStatus.error}</p>
              <div className="space-y-2">
                <_Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRetryConnection}
                className="border-red-300 text-red-700 hover:bg-red-50">

                  <_Icon name="RefreshCw" size={16} className="mr-1" />
                  Retry Connection
                </_Button>
                <div className="text-xs text-red-600">
                  <p>Common fixes:</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>Check your internet connection</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      }

      {/* Success Message */}
      {displaySuccess &&
      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-700 flex items-center">
            <_Icon name="CheckCircle" size={16} className="mr-2" />
            {displaySuccess}
          </p>
        </div>
      }

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
            Email Address
          </label>
          <_Input
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleInputChange}
            disabled={isLoading}
            required
            className={errors.email ? 'border-error' : ''}
            autoComplete="email" />

          {errors.email &&
          <p className="mt-1 text-sm text-error flex items-center">
              <_Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.email}
            </p>
          }
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
            Password
          </label>
          <div className="relative">
            <_Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={isLoading}
              required
              className={`pr-12 ${errors.password ? 'border-error' : ''}`}
              autoComplete="current-password" />

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-text-primary"
              disabled={isLoading}
              aria-label={showPassword ? 'Hide password' : 'Show password'}>

              <_Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
            </button>
          </div>
          {errors.password &&
          <p className="mt-1 text-sm text-error flex items-center">
              <_Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.password}
            </p>
          }
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <_Input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleInputChange}
              disabled={isLoading}
              className="w-4 h-4 mr-2" />

            <span className="text-sm text-text-secondary">Remember me</span>
          </label>
          
          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm text-primary hover:text-primary-600 transition-colors duration-200 disabled:opacity-50"
            disabled={isLoading}>

            Forgot password?
          </button>
        </div>

        {/* Submit Error */}
        {displayError && (
        <div className="p-3 bg-error-50 border border-error-200 rounded-lg">
          {/* Special handling for email verification errors */}
          {isEmailVerificationError ? (
            <div className="space-y-3">
              <div className="flex items-start">
                <_Icon name="AlertCircle" size={16} className="mr-2 mt-0.5 flex-shrink-0 text-error" />
                <span className="text-sm text-error">{displayError}</span>
              </div>
              
              <div className="mt-2 border-t border-error-200 pt-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">Didn't receive the email?</p>
                  <_Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleResendVerification(formData.email)}
                    disabled={isLoading || isResending}
                    className="border-primary text-primary hover:bg-primary-50 flex items-center"
                  >
                    {isResending ? (
                      <>
                        <_Icon name="Loader" size={14} className="mr-1 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <_Icon name="RefreshCw" size={14} className="mr-1" />
                        Resend email
                      </>
                    )}
                  </_Button>
                </div>
                
                {resendStatus && (
                  <div className={`mt-2 p-2 rounded ${resendStatus.success ? 'bg-green-50' : 'bg-error-50'}`}>
                    <p className={`text-sm flex items-center ${resendStatus.success ? 'text-green-700' : 'text-error'}`}>
                      <_Icon 
                        name={resendStatus.success ? "CheckCircle" : "AlertTriangle"} 
                        size={14} 
                        className="mr-1" 
                      />
                      {resendStatus.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-error flex items-start">
              <_Icon name="AlertCircle" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
              <span>{displayError}</span>
            </p>
          )}
        </div>
      )}  

        {/* Submit Button */}
        <_Button
          type="submit"
          variant="primary"
          fullWidth
          loading={isLoading}
          disabled={isLoading || connectionStatus && !connectionStatus.connected}
          className="py-3">

          {isLoading ? 'Signing in...' : 'Sign In'}
        </_Button>

        {/* Connection Status Footer */}
        {connectionStatus?.connected &&
        <div className="text-center text-xs text-green-600 flex items-center justify-center">
            <_Icon name="CheckCircle" size={12} className="mr-1" />
            {debugInfo.isDevelopment 
              ? 'Development mode: Services simulated' 
              : 'All services connected and operational'}
          </div>
        }
        
      </form>
    </div>);

};

export default LoginForm;

